import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router";
import { MenuOutlined, PlusOutlined } from "@ant-design/icons";
import {
	Form,
	Button,
	Tree,
	Flex,
	Space,
	Card,
	Typography,
	notification,
	Select,
	Spin,
	Checkbox,
	DatePicker,
	Modal,
	Input,
	Tag,
} from "antd";
import { UserContext } from "../../../components/privateroute";
import ConnectedTickets from "../connectedTickets";
import Areas from "../../../components/Areas";
import SpecialTimeSetter from "../specialTime/specialTimeSetter";
import SendNotifications from "../sendNotifications";
import AdditionalFields from "../additionalFields";
import CanWrapper from "../../../../../components/Can";
import getUserFriendlyErrorMessage from "../../../../../functions/getUserFriendlyErrorMessage";
import dayjs from "dayjs";
const { Text } = Typography;

function isValueInArray(array, value) {
	return array.some((element) => String(element?.value) === String(value));
}

function ensureCurrentStatusOption(options, ticket) {
	const safeOptions = Array.isArray(options) ? options : [];
	const currentId = ticket?.ticketstatusid;
	const currentTitle = ticket?.statustitle;

	if (currentId == null) return safeOptions;
	if (
		safeOptions.some(
			(option) => String(option?.value) === String(currentId),
		)
	) {
		return safeOptions;
	}
	if (!currentTitle) return safeOptions;

	return [
		...safeOptions,
		{
			value: currentId,
			label: currentTitle,
		},
	];
}

function normalizeStatusLabel(value) {
	return String(value || "")
		.trim()
		.toLowerCase();
}

function isStatusLabel(label, target) {
	return normalizeStatusLabel(label) === normalizeStatusLabel(target);
}

function isActiveStatusLabel(label) {
	const normalized = normalizeStatusLabel(label);
	return [
		normalizeStatusLabel("Nowy"),
		normalizeStatusLabel("Realizacja"),
		normalizeStatusLabel("Realizacja w terenie"),
		normalizeStatusLabel("Po kontakcie"),
		normalizeStatusLabel("Po wizycie"),
		normalizeStatusLabel("Do oceny"),
		normalizeStatusLabel("Zawieszone"),
		normalizeStatusLabel("CRM częściowo"),
	].includes(normalized);
}

function getStatusLabelByValue(options, value, fallbackLabel) {
	const safeOptions = Array.isArray(options) ? options : [];
	const match = safeOptions.find((x) => x?.value == value);
	if (match?.label) return match.label;
	return fallbackLabel || "";
}

function shouldProposeReminderForStatus(label) {
	const normalized = normalizeStatusLabel(label);
	return [
		normalizeStatusLabel("Realizacja"),
		normalizeStatusLabel("Realizacja w terenie"),
		normalizeStatusLabel("Po kontakcie"),
		normalizeStatusLabel("Po wizycie"),
		normalizeStatusLabel("Do oceny"),
		normalizeStatusLabel("CRM częściowo"),
		normalizeStatusLabel("Zawieszone"),
	].includes(normalized);
}

function buildReminderTimestamp(choice, customDate) {
	if (choice === "none") return null;
	if (choice === "custom") {
		if (!customDate) return null;
		return dayjs(customDate)
			.hour(9)
			.minute(0)
			.second(0)
			.millisecond(0)
			.unix();
	}
	const days = choice === "30" ? 30 : 7;
	return dayjs()
		.add(days, "day")
		.hour(9)
		.minute(0)
		.second(0)
		.millisecond(0)
		.unix();
}

function getStatusMetaFromTicket(ticket) {
	const source = ticket?.statusMeta || {};
	return {
		closeReason: source.closeReason || "",
		suspendReason: source.suspendReason || "",
		suspendReviewDate: source.suspendReviewDate || null,
		suspendNoReviewDate: Boolean(source.suspendNoReviewDate),
		evaluationReason: source.evaluationReason || "",
		crmPartialReason: source.crmPartialReason || "",
		reopenReason: source.reopenReason || "",
	};
}

const SideBar = (props) => {
	const globalUser = React.useContext(UserContext);
	const {
		saveCredentialsPost,
		special,
		setSpecial,
		posts,
		setPosts,
		ticket,
		setTicket,
		showLogs,
		setShowLogs,
		setReload,
		reload,
		areas,
		setAreas,
		setLoader,
		loader,
		readOnly = false,
		backPath = "/sprawy",
	} = props;
	const [credentials, setCredentials] = useState(ticket);
	const navigate = useNavigate();
	const [values, setValues] = useState(null);
	const [mobileOpen, setMobileOpen] = useState(false);
	const [fields, setFields] = useState({});
	const [saveLoading, setSaveLoading] = useState(false);
	const [showAccidentdate, setShowAccidentdate] = useState(true);
	const [signingLoading, setSigningLoading] = useState(false);
	const [statusMeta, setStatusMeta] = useState({
		...getStatusMetaFromTicket(ticket),
	});
	const [reminderProposalOpen, setReminderProposalOpen] = useState(false);
	const [reminderChoice, setReminderChoice] = useState("7");
	const [reminderCustomDate, setReminderCustomDate] = useState(
		dayjs().add(7, "day"),
	);
	const [pendingSendType, setPendingSendType] = useState(null);
	const pendingAfterSaveRef = useRef(null);
	const [competitionModalOpen, setCompetitionModalOpen] = useState(false);
	const [competitionNameDraft, setCompetitionNameDraft] = useState("");

	const markSigned = async () => {
		try {
			if (!ticket?.ticketid) return;
			setSigningLoading(true);
			await axios.post("/sprawa/contract/signed", {
				ticketid: ticket.ticketid,
				userid: globalUser?.user?.mlg_users_id,
			});
			notification.success({
				message: "Zapisano: Umowa podpisana",
				duration: 2,
			});
			setReload(!reload);
		} catch (e) {
			notification.error({
				message: "Nie udało się zapisać",
				duration: 3,
			});
		} finally {
			setSigningLoading(false);
		}
	};

	const getValues = (e, y) => {
		if (e == "userid" || e == "watchers") {
			var data = values[e];
			if (e == "userid") {
				const valueToCheck = credentials[y]; // The value you're checking for

				// Check if the value exists in the object and modify the `hidden` property
				data = data.map((item) => {
					if (item.value === valueToCheck) {
						return { ...item, hidden: false }; // Change hidden to 0 for the specified value
					}
					return item;
				});

				// Remove all elements with `hidden` equal to 1
				data = data.filter((item) => item.hidden === false);
				return data;
			}
			if (e == "watchers") {
				const valuesToCheck = credentials[y]; // The values you're checking for as an array

				// Check if the value exists in the valuesToCheck array and modify the `hidden` property
				if (valuesToCheck) {
					data = data.map((item) => {
						if (valuesToCheck.includes(item.value)) {
							return { ...item, hidden: false }; // Change hidden to 0 for the specified values
						}
						return item;
					});
				}

				// Remove all elements with `hidden` equal to 1
				data = data.filter((item) => item.hidden === false);
				return data;
			}
		} else if (e == "tags") {
			// console.log(values[e])
			var data = values[e];
			const valuesToCheck = credentials[y]; // The values you're checking for as an array
			// console.log(valuesToCheck)

			// Check if the value exists in the valuesToCheck array and modify the `hidden` property
			if (valuesToCheck) {
				data = data.map((item) => {
					if (valuesToCheck.includes(item.value)) {
						return { ...item, hidden: false }; // Change hidden to 0 for the specified values
					}
					return item;
				});
			}

			// Remove all elements with `hidden` equal to 1
			data = data.filter((item) => item.hidden === false);
			return data;
		} else {
			if (e == "ticketstatusid") {
				return ensureCurrentStatusOption(values[e], ticket);
			}
			return values[e];
		}
	};

	useEffect(() => {
		setValues(null);
		const fetchData = async () => {
			const response = await axios.post("/sprawa/default/values", {
				departmentid: credentials.departmentid,
			});
			setValues(response.data);
		};
		fetchData();
	}, [credentials.departmentid, ticket]);

	useEffect(() => {
		const initialCompetitionNames = Array.isArray(ticket?.competitionnames)
			? ticket.competitionnames
			: Array.isArray(ticket?.competition)
				? ticket.competition
						.map((x) => String(x?.name || "").trim())
						.filter(Boolean)
				: [];
		setCredentials({
			...ticket,
			competitionnames: Array.from(new Set(initialCompetitionNames)),
		});
		setShowAccidentdate(ticket.accident_dateline ? true : false);
		setStatusMeta(getStatusMetaFromTicket(ticket));
		setReminderProposalOpen(false);
		setReminderChoice("7");
		setReminderCustomDate(dayjs().add(7, "day"));
		setPendingSendType(null);
		pendingAfterSaveRef.current = null;
		setCompetitionModalOpen(false);
		setCompetitionNameDraft("");
	}, [ticket]);

	const handleDataChange = (value, key) => {
		setCredentials((prevCredentials) => ({
			...prevCredentials,
			[key]: value == "NaN" ? null : value,
		}));
	};

	const handleStatusMetaChange = (key, value) => {
		setStatusMeta((prev) => ({
			...prev,
			[key]: value,
		}));
	};

	const createStatusReminder = async ({
		statusLabel,
		choice,
		customDate,
	}) => {
		const timestamp = buildReminderTimestamp(choice, customDate);
		if (!timestamp) return;
		await axios.post("/reminders/add", {
			eventname: `Przypomnienie statusu: ${statusLabel}`,
			startdateline: timestamp,
			enddateline: timestamp,
			sms: "0",
			mail: "1",
			priority: "2",
			userid: globalUser.user.mlg_users_id,
			ticketid: credentials?.ticketid,
			assigneeMode: "owner",
			addusers: [],
		});
	};

	const saveCredentials = async (sendType = null, options = {}) => {
		try {
			const {
				skipReminderPrompt = false,
				reminderChoice: chosenReminderChoice = "none",
				reminderDate: chosenReminderDate = null,
				onAfterSave = null,
			} = options;
			const selectedStatusLabel = getStatusLabelByValue(
				values?.ticketstatusid,
				credentials?.ticketstatusid,
				credentials?.statustitle,
			);
			const oldStatusLabel = ticket?.statustitle || "";
			const statusChanged =
				normalizeStatusLabel(selectedStatusLabel) !==
				normalizeStatusLabel(oldStatusLabel);

			if (
				!skipReminderPrompt &&
				!readOnly &&
				statusChanged &&
				shouldProposeReminderForStatus(selectedStatusLabel)
			) {
				setPendingSendType(sendType);
				pendingAfterSaveRef.current =
					typeof onAfterSave === "function" ? onAfterSave : null;
				setReminderChoice("7");
				setReminderCustomDate(dayjs().add(7, "day"));
				setReminderProposalOpen(true);
				return "deferred";
			}

			setSaveLoading(true);

			if (isStatusLabel(selectedStatusLabel, "Koniec")) {
				if ((statusMeta.closeReason || "").trim().length < 20) {
					notification.warning({
						message:
							"Dla statusu 'Koniec' wpisz powód zakończenia (minimum 20 znaków)",
						duration: 4,
					});
					return;
				}
			}

			if (isStatusLabel(selectedStatusLabel, "Do oceny")) {
				if (!(statusMeta.evaluationReason || "").trim()) {
					notification.warning({
						message:
							"Dla statusu 'Do oceny' opisz, co dokładnie wymaga oceny",
						duration: 4,
					});
					return;
				}
			}

			if (isStatusLabel(selectedStatusLabel, "Zawieszone")) {
				const hasDate =
					Number.isFinite(Number(statusMeta.suspendReviewDate)) &&
					Number(statusMeta.suspendReviewDate) > 0;
				if (!(statusMeta.suspendReason || "").trim()) {
					notification.warning({
						message:
							"Dla statusu 'Zawieszone' wpisz powód zawieszenia",
						duration: 4,
					});
					return;
				}
				if (!hasDate && !statusMeta.suspendNoReviewDate) {
					notification.warning({
						message:
							"Dla 'Zawieszone' podaj datę ponownego sprawdzenia albo zaznacz brak możliwości określenia daty",
						duration: 4,
					});
					return;
				}
			}

			const reopenFromFinal =
				(isStatusLabel(oldStatusLabel, "Koniec") ||
					isStatusLabel(oldStatusLabel, "CRM zamknięte")) &&
				isActiveStatusLabel(selectedStatusLabel) &&
				normalizeStatusLabel(oldStatusLabel) !==
					normalizeStatusLabel(selectedStatusLabel);
			if (reopenFromFinal && !(statusMeta.reopenReason || "").trim()) {
				notification.warning({
					message:
						"Wznowienie statusu końcowego wymaga podania powodu wznowienia",
					duration: 4,
				});
				return;
			}

			// Wait for the server to complete the update
			const response = await axios.post("/sprawa/save/values", {
				userid: globalUser.user.mlg_users_id,
				credentials: credentials,
				ticketid: ticket,
				areas: areas,
				fields: fields,
				statusMeta,
				sendtype: sendType,
			});

			// Wait a little to ensure everything is processed, but only if necessary
			await new Promise((resolve) => setTimeout(resolve, 200));

			// Handle the response and show success or failure notifications
			if (response.status === 200) {
				notification.success({
					message: response.data.message,
					duration: 3,
				});
				if (
					statusChanged &&
					skipReminderPrompt &&
					chosenReminderChoice !== "none"
				) {
					try {
						await createStatusReminder({
							statusLabel: selectedStatusLabel,
							choice: chosenReminderChoice,
							customDate: chosenReminderDate,
						});
						notification.success({
							message: "Dodano przypomnienie po zmianie statusu",
							duration: 3,
						});
					} catch (error) {
						notification.warning({
							message:
								"Status zapisany, ale nie udało się dodać przypomnienia",
							duration: 4,
						});
					}
				}
			} else {
				notification.error({
					message: response.data.message,
					duration: 3,
				});
				return false;
			}
			if (typeof onAfterSave === "function") {
				await onAfterSave();
			}
			return true;
		} catch (error) {
			notification.error({
				message: getUserFriendlyErrorMessage(
					error,
					"Nie udalo sie zapisac zmian",
				),
				duration: 3,
			});
			return false;
		} finally {
			// Disable the loading state
			setSaveLoading(false);
		}
	};

	const confirmReminderProposal = async () => {
		if (reminderChoice === "custom" && !reminderCustomDate) {
			notification.warning({
				message: "Wybierz datę przypomnienia",
				duration: 3,
			});
			return;
		}
		const executed = await saveCredentials(pendingSendType, {
			skipReminderPrompt: true,
			reminderChoice,
			reminderDate: reminderCustomDate,
			onAfterSave: pendingAfterSaveRef.current,
		});
		if (executed) {
			setReminderProposalOpen(false);
			setPendingSendType(null);
			pendingAfterSaveRef.current = null;
		}
	};

	const onChangeCheckbox = (e) => {
		setShowLogs(e.target.checked);
	};

	const addCompetitionName = () => {
		const nextName = String(competitionNameDraft || "")
			.trim()
			.replace(/\s+/g, " ");
		if (!nextName) return;
		const current = Array.isArray(credentials?.competitionnames)
			? credentials.competitionnames
			: [];
		const exists = current.some(
			(item) =>
				String(item || "").toLowerCase() === nextName.toLowerCase(),
		);
		if (exists) {
			notification.info({
				message: "Ta konkurencja jest już dodana",
				duration: 2,
			});
			return;
		}
		handleDataChange([...current, nextName], "competitionnames");
		handleDataChange([], "competitionids");
		handleDataChange(1, "competitionis");
		setCompetitionNameDraft("");
		setCompetitionModalOpen(false);
	};

	const removeCompetitionName = (nameToRemove) => {
		const current = Array.isArray(credentials?.competitionnames)
			? credentials.competitionnames
			: [];
		handleDataChange(
			current.filter((name) => name !== nameToRemove),
			"competitionnames",
		);
		handleDataChange([], "competitionids");
	};

	const filterOption = (input, option) =>
		(option?.label ?? "").toLowerCase().includes(input.toLowerCase());
	const selectedStatusLabel = getStatusLabelByValue(
		values?.ticketstatusid,
		credentials?.ticketstatusid,
		credentials?.statustitle,
	);
	const oldStatusLabel = ticket?.statustitle || "";
	const requiresReopenReason =
		(isStatusLabel(oldStatusLabel, "Koniec") ||
			isStatusLabel(oldStatusLabel, "CRM zamknięte")) &&
		isActiveStatusLabel(selectedStatusLabel) &&
		normalizeStatusLabel(oldStatusLabel) !==
			normalizeStatusLabel(selectedStatusLabel);

	return (
		<>
			<Modal
				title="Dodaj konkurencję"
				open={competitionModalOpen}
				onCancel={() => {
					setCompetitionModalOpen(false);
					setCompetitionNameDraft("");
				}}
				onOk={addCompetitionName}
				okText="Dodaj"
				cancelText="Anuluj"
			>
				<Input
					placeholder="Wpisz nazwę konkurencji"
					value={competitionNameDraft}
					onChange={(e) => setCompetitionNameDraft(e.target.value)}
					onPressEnter={addCompetitionName}
				/>
			</Modal>
			<Modal
				title="Ustawić przypomnienie po zmianie statusu?"
				open={reminderProposalOpen}
				onCancel={() => {
					setReminderProposalOpen(false);
					setPendingSendType(null);
					pendingAfterSaveRef.current = null;
				}}
				onOk={confirmReminderProposal}
				okText="Zapisz"
				cancelText="Anuluj"
				confirmLoading={saveLoading}
			>
				<Form layout="vertical">
					<Form.Item label="Wybierz termin przypomnienia">
						<Select
							value={reminderChoice}
							onChange={(value) => setReminderChoice(value)}
							options={[
								{ value: "7", label: "Przypomnij za 7 dni" },
								{ value: "30", label: "Przypomnij za 30 dni" },
								{ value: "custom", label: "Wybierz inną datę" },
								{
									value: "none",
									label: "Nie ustawiaj przypomnienia",
								},
							]}
						/>
					</Form.Item>
					{reminderChoice === "custom" ? (
						<Form.Item label="Data przypomnienia">
							<DatePicker
								style={{ width: "100%" }}
								value={reminderCustomDate}
								onChange={(date) => setReminderCustomDate(date)}
								disabledDate={(current) =>
									current && current < dayjs().startOf("day")
								}
							/>
						</Form.Item>
					) : null}
				</Form>
			</Modal>
			{mobileOpen ? (
				<></>
			) : (
				<div
					className="button-sidebaropener"
					onClick={() => setMobileOpen(!mobileOpen)}
				>
					<MenuOutlined></MenuOutlined>
					<div>Dane sprawy</div>
				</div>
			)}
			<div
				className={
					mobileOpen
						? "sidebar-wrapper side-opened"
						: "sidebar-wrapper"
				}
				direction="vertical"
				style={{
					zIndex: "10",
					paddingBottom: "20px",
					overflowY: "auto",
					boxShadow: "0px 2px 8px 0px rgba(0, 0, 0, 0.15)",
					height: "100%",
					width: "300px",
					flexShrink: "none",
					alignItems: "flex-start",
					display: "flex",
					flexDirection: "column",
					gap: "20px",
				}}
			>
				<div style={{ width: "100%" }}>
					<Spin spinning={values == null}>
						<div
							style={{
								padding: "20px 10px",
								background: "white",
								position: "sticky",
								top: "0",
								zIndex: 5,
								boxShadow:
									"0px 4px 4px 0px rgba(0, 0, 0, 0.12)",
								marginBottom: "10px",
							}}
						>
							<div
								className="sidebaropener"
								onClick={() => setMobileOpen(!mobileOpen)}
							>
								Zwiń panel boczny
							</div>
							{!readOnly ? (
								<SpecialTimeSetter
									loader={loader}
									setLoader={setLoader}
									ticketid={ticket}
									reload={reload}
									setReload={setReload}
									special={special}
									setSpecial={setSpecial}
								></SpecialTimeSetter>
							) : null}
							<CanWrapper perms={["can_access_logs"]}>
								<Checkbox
									style={{
										flexDirection: "row-reverse",
										float: "right",
										marginBottom: "5px",
									}}
									onChange={onChangeCheckbox}
								>
									Pokaż logi systemowe
								</Checkbox>
							</CanWrapper>
							<Flex style={{ width: "100%", gap: "10px" }}>
								<Button onClick={() => navigate(backPath)}>
									Powrót
								</Button>
								{/* <Button style={{width: "100%"}} onClick={saveCredentials} loading={saveLoading}>Zapisz</Button> */}
								{!readOnly ? (
									<CanWrapper perms={["can_save_ticket"]}>
										<SendNotifications
											styleButton={{ width: "100%" }}
											saveCredentials={saveCredentials}
											saveCredentialsPost={
												saveCredentialsPost
											}
											saveLoading={saveLoading}
											setSaveLoading={setSaveLoading}
											setReload={setReload}
											reload={reload}
										></SendNotifications>
									</CanWrapper>
								) : null}
							</Flex>
						</div>
						<div style={{ padding: "5px 10px", width: "100%" }}>
							<div
								style={{
									borderRadius: "5px",
									padding: "10px",
									border: "1px solid #E2E2E2",
									width: "100%",
									display: "flex",
									flexDirection: "column",
									gap: "20px",
								}}
							>
								<Text
									style={{
										fontSize: "14px",
										fontWeight: "600",
									}}
								>
									Dane zgłoszenia
								</Text>
								{values != null ? (
									<>
										<Form.Item
											label="Przypisany pracownik"
											style={{ margin: "0px" }}
										>
											<Select
												name={"owneruserid"}
												showSearch
												onChange={(e) =>
													handleDataChange(
														e,
														"owneruserid",
													)
												}
												disabled={readOnly}
												options={[
													{
														value: 0,
														label: "BRAK PRZYPISANEGO PRACOWNIKA",
														hidden: false,
													},
													...getValues(
														"userid",
														"owneruserid",
													),
												]}
												defaultValue={
													credentials["owneruserid"]
												}
												filterOption={filterOption}
												size="large"
												required
											/>
										</Form.Item>
										<Form.Item
											label="Sprawa przypisana do list"
											style={{ margin: "0px" }}
										>
											<Select
												mode="multiple"
												name={"lists"}
												onChange={(e) =>
													handleDataChange(e, "lists")
												}
												disabled={readOnly}
												options={getValues(
													"lists",
													"lists",
												)}
												defaultValue={
													credentials["lists"]
												}
												size="large"
												filterOption={filterOption}
												required
											/>
										</Form.Item>
										<Form.Item
											label="Status"
											style={{ margin: "0px" }}
										>
											<Select
												name={"ticketstatusid"}
												onChange={(e) =>
													handleDataChange(
														e,
														"ticketstatusid",
													)
												}
												disabled={readOnly}
												options={getValues(
													"ticketstatusid",
												)}
												value={
													isValueInArray(
														getValues(
															"ticketstatusid",
														),
														credentials[
															"ticketstatusid"
														],
													)
														? credentials[
																"ticketstatusid"
															]
														: getValues(
																"ticketstatusid",
															)[0].value
												}
												size="large"
												required
											/>
										</Form.Item>
										{isStatusLabel(
											selectedStatusLabel,
											"Koniec",
										) ? (
											<Form.Item
												label="Powód zakończenia sprawy w MSP (min. 20 znaków)"
												style={{ margin: "0px" }}
											>
												<Input.TextArea
													rows={3}
													disabled={readOnly}
													value={
														statusMeta.closeReason
													}
													onChange={(e) =>
														handleStatusMetaChange(
															"closeReason",
															e.target.value,
														)
													}
												/>
											</Form.Item>
										) : null}
										{isStatusLabel(
											selectedStatusLabel,
											"Do oceny",
										) ? (
											<Form.Item
												label="Co dokładnie wymaga oceny?"
												style={{ margin: "0px" }}
											>
												<Input.TextArea
													rows={3}
													disabled={readOnly}
													value={
														statusMeta.evaluationReason
													}
													onChange={(e) =>
														handleStatusMetaChange(
															"evaluationReason",
															e.target.value,
														)
													}
												/>
											</Form.Item>
										) : null}
										{isStatusLabel(
											selectedStatusLabel,
											"CRM częściowo",
										) ? (
											<Form.Item
												label="Co zostało przekazane do CRM i co pozostaje do dalszych działań?"
												style={{ margin: "0px" }}
											>
												<Input.TextArea
													rows={3}
													disabled={readOnly}
													value={
														statusMeta.crmPartialReason
													}
													onChange={(e) =>
														handleStatusMetaChange(
															"crmPartialReason",
															e.target.value,
														)
													}
												/>
											</Form.Item>
										) : null}
										{isStatusLabel(
											selectedStatusLabel,
											"Zawieszone",
										) ? (
											<>
												<Form.Item
													label="Powód zawieszenia"
													style={{ margin: "0px" }}
												>
													<Input.TextArea
														rows={3}
														disabled={readOnly}
														value={
															statusMeta.suspendReason
														}
														onChange={(e) =>
															handleStatusMetaChange(
																"suspendReason",
																e.target.value,
															)
														}
													/>
												</Form.Item>
												<Form.Item
													label="Data ponownego sprawdzenia"
													style={{ margin: "0px" }}
												>
													<DatePicker
														style={{
															width: "100%",
														}}
														size="large"
														disabled={
															readOnly ||
															statusMeta.suspendNoReviewDate
														}
														value={
															statusMeta.suspendReviewDate
																? dayjs.unix(
																		statusMeta.suspendReviewDate,
																	)
																: null
														}
														onChange={(date) =>
															handleStatusMetaChange(
																"suspendReviewDate",
																date
																	? dayjs(
																			date,
																		).unix()
																	: null,
															)
														}
													/>
												</Form.Item>
												<Checkbox
													disabled={readOnly}
													checked={
														statusMeta.suspendNoReviewDate
													}
													onChange={(e) =>
														handleStatusMetaChange(
															"suspendNoReviewDate",
															e.target.checked,
														)
													}
												>
													Nie da się obecnie określić
													daty ponownego sprawdzenia
												</Checkbox>
											</>
										) : null}
										{requiresReopenReason ? (
											<Form.Item
												label="Powód wznowienia sprawy"
												style={{ margin: "0px" }}
											>
												<Input.TextArea
													rows={3}
													disabled={readOnly}
													value={
														statusMeta.reopenReason
													}
													onChange={(e) =>
														handleStatusMetaChange(
															"reopenReason",
															e.target.value,
														)
													}
												/>
											</Form.Item>
										) : null}
										{showAccidentdate ? (
											<>
												<CanWrapper
													perms={[
														"can_change_accident_date",
													]}
												>
													<Form.Item
														label="Data zdarzenia"
														style={{
															margin: "0px",
														}}
													>
														<DatePicker
															style={{
																width: "100%",
															}}
															size="large"
															disabled={readOnly}
															defaultValue={
																credentials[
																	"accident_dateline"
																] != "null" &&
																credentials[
																	"accident_dateline"
																]
																	? dayjs.unix(
																			credentials[
																				"accident_dateline"
																			],
																		)
																	: ""
															}
															onChange={(
																date,
																datestring,
															) =>
																handleDataChange(
																	dayjs(
																		date,
																	).unix(),
																	"accident_dateline",
																)
															}
															disabledDate={(
																current,
															) =>
																current &&
																current >
																	dayjs().endOf(
																		"day",
																	)
															}
														/>
													</Form.Item>
												</CanWrapper>
											</>
										) : (
											<>
												<Form.Item
													label="Data zdarzenia"
													style={{ margin: "0px" }}
												>
													<DatePicker
														style={{
															width: "100%",
														}}
														size="large"
														disabled={readOnly}
														defaultValue={
															credentials[
																"accident_dateline"
															] != "null" &&
															credentials[
																"accident_dateline"
															]
																? dayjs.unix(
																		credentials[
																			"accident_dateline"
																		],
																	)
																: ""
														}
														onChange={(
															date,
															datestring,
														) =>
															handleDataChange(
																dayjs(
																	date,
																).unix(),
																"accident_dateline",
															)
														}
														disabledDate={(
															current,
														) =>
															current &&
															current >
																dayjs().endOf(
																	"day",
																)
														}
													/>
												</Form.Item>
											</>
										)}
										<Form.Item
											label="Priorytet"
											style={{ margin: "0px" }}
										>
											<Select
												name={"priorityid"}
												onChange={(e) =>
													handleDataChange(
														e,
														"priorityid",
													)
												}
												disabled={readOnly}
												options={getValues(
													"priorityid",
												)}
												defaultValue={
													credentials["priorityid"]
												}
												size="large"
												required
											/>
										</Form.Item>
										<Form.Item
											label="Obserwatorzy"
											style={{ margin: "0px" }}
										>
											<Select
												mode="multiple"
												name={"watchers"}
												onChange={(e) =>
													handleDataChange(
														e,
														"watchers",
													)
												}
												disabled={readOnly}
												options={getValues(
													"watchers",
													"watchers",
												)}
												defaultValue={
													credentials["watchers"]
												}
												size="large"
												filterOption={filterOption}
												required
											/>
										</Form.Item>
										<Form.Item
											label="Kody rejestracyjne powiatu"
											style={{ margin: "0px" }}
										>
											<Select
												mode="multiple"
												name={"registerplates"}
												onChange={(e) =>
													handleDataChange(
														e,
														"registerplates",
													)
												}
												disabled={readOnly}
												options={getValues(
													"registerplates",
												)}
												defaultValue={
													credentials[
														"registerplates"
													]
												}
												size="large"
												filterOption={filterOption}
												required
											/>
										</Form.Item>
										<Form.Item
											label="Tagi"
											style={{ margin: "0px" }}
										>
											<Select
												mode="multiple"
												name={"tags"}
												onChange={(e) =>
													handleDataChange(e, "tags")
												}
												disabled={readOnly}
												options={getValues(
													"tags",
													"tags",
												)}
												defaultValue={
													credentials["tags"]
												}
												size="large"
												filterOption={filterOption}
												required
											/>
										</Form.Item>
										<Form.Item
											label="Konkurencja"
											style={{ margin: "0px" }}
										>
											<Checkbox
												disabled={readOnly}
												checked={Boolean(
													credentials?.competitionis,
												)}
												onChange={(e) => {
													const checked =
														e.target.checked;
													handleDataChange(
														checked ? 1 : 0,
														"competitionis",
													);
													if (!checked) {
														handleDataChange(
															[],
															"competitionnames",
														);
														handleDataChange(
															[],
															"competitionids",
														);
													}
												}}
											>
												Konkurencja
											</Checkbox>
										</Form.Item>
										<Form.Item
											label="Podmioty konkurencyjne"
											style={{ margin: "0px" }}
										>
											<Flex
												gap={8}
												wrap="wrap"
												align="center"
											>
												{(
													credentials?.competitionnames ||
													[]
												).map((name) => (
													<Tag
														key={name}
														closable={
															!readOnly &&
															Boolean(
																credentials?.competitionis,
															)
														}
														onClose={(e) => {
															e.preventDefault();
															removeCompetitionName(
																name,
															);
														}}
													>
														{name}
													</Tag>
												))}
												<Button
													type="dashed"
													icon={<PlusOutlined />}
													disabled={
														readOnly ||
														!Boolean(
															credentials?.competitionis,
														)
													}
													onClick={() =>
														setCompetitionModalOpen(
															true,
														)
													}
												>
													Dodaj
												</Button>
											</Flex>
										</Form.Item>
										<Form.Item
											label="Kody pocztowe"
											style={{ margin: "0px" }}
										>
											<Select
												mode="multiple"
												name={"postcodes"}
												onChange={(e) =>
													handleDataChange(
														e,
														"postcodes",
													)
												}
												disabled={readOnly}
												options={getValues("postcodes")}
												defaultValue={
													credentials["postcodes"]
												}
												value={credentials["postcodes"]}
												size="large"
												filterOption={filterOption}
												required
											/>
										</Form.Item>
									</>
								) : (
									<></>
								)}
							</div>
						</div>
					</Spin>
				</div>
				{/* <div style={{padding: '5px 10px', width: '100%'}}>
            <AdditionalFields setFields={setFields} fields={fields} reload={reload} setReload={setReload} ticketid={ticket.ticketid}></AdditionalFields>
            </div> */}
				{!readOnly ? (
					<>
						<div style={{ padding: "5px 10px", width: "100%" }}>
							<Button
								style={{ width: "100%" }}
								loading={signingLoading}
								onClick={markSigned}
								type="primary"
							>
								Umowa podpisana
							</Button>
						</div>
						<div style={{ padding: "5px 10px", width: "100%" }}>
							<ConnectedTickets
								setReload={setReload}
								reload={reload}
								credentials={credentials}
							></ConnectedTickets>
						</div>
						<div style={{ padding: "5px 10px", width: "100%" }}>
							<Areas
								areas={areas}
								setAreas={setAreas}
							></Areas>
						</div>
					</>
				) : null}
			</div>
		</>
	);
};
export default SideBar;
